import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useChatSidebarStore } from '@air/feature-ag-ui-chat';

import { usePendingSkillRunStore } from '~/components/v2/store/features/skill-onboarding/usePendingSkillRunStore';
import { QueryParamNames } from '~/constants/search';
import { isSkillOnboardingImageName, type SkillOnboardingImageName } from '~/constants/skillOnboardingImages';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { pushWithExistingQuery } from '~/utils/PathUtils';
import { isMobileAgent } from '~/utils/PlatformHelpers';

const getStringFromQuery = (value: string | string[] | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};

const decodeCanvasName = (canvasName: string) => {
  try {
    return decodeURIComponent(canvasName);
  } catch {
    return canvasName;
  }
};

type SkillFlowParams = {
  skillSlashCommand: string;
  imageName: SkillOnboardingImageName;
};

const getSkillFlowParams = (skillName: string | undefined, imageName: string | undefined): SkillFlowParams | null => {
  if (!skillName || !imageName) {
    return null;
  }
  if (!isSkillOnboardingImageName(imageName)) {
    return null;
  }

  return { skillSlashCommand: skillName, imageName };
};

export const useCreateCanvasFromSearchParams = () => {
  const router = useRouter();
  const boardId = useURLBoardIdSelector();
  const { currentWorkspace } = useCurrentWorkspace();
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const { commitDraftCanvas, openDraftCanvas } = useDraftCanvas();
  const processedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!router.isReady || !generativeCanvasEnabled || !currentWorkspace?.id) {
      return;
    }

    const canvasNameQueryParam = router.query[QueryParamNames.canvasName];
    const canvasUploadDataUriQueryParam = router.query[QueryParamNames.canvasUploadDataUri];

    if (!canvasNameQueryParam) {
      processedKeyRef.current = null;
      return;
    }

    if (isMobileAgent && canvasUploadDataUriQueryParam) {
      return;
    }

    const canvasName = getStringFromQuery(canvasNameQueryParam);

    if (!canvasName) {
      return;
    }

    const skillName = getStringFromQuery(router.query[QueryParamNames.skillName]);
    const imageName = getStringFromQuery(router.query[QueryParamNames.imageName]);
    const skillFlow = getSkillFlowParams(skillName, imageName);

    // Idempotency key includes all three params so re-entry under a changed param set re-runs.
    const key = `${canvasName}:${skillName ?? ''}:${imageName ?? ''}`;

    if (processedKeyRef.current === key) {
      return;
    }

    processedKeyRef.current = key;

    const decodedCanvasName = decodeCanvasName(canvasName).trim();

    if (!decodedCanvasName) {
      return;
    }

    void (async () => {
      await pushWithExistingQuery({
        newQuery: {
          [QueryParamNames.canvasName]: undefined,
          [QueryParamNames.skillName]: undefined,
          [QueryParamNames.imageName]: undefined,
        },
        method: 'replace',
      });

      openDraftCanvas({
        boardId,
        analyticsContext: {
          source: 'canvases',
          board_id: boardId,
        },
      });

      const { canvasId } = await commitDraftCanvas([], decodedCanvasName);

      if (skillFlow) {
        useChatSidebarStore.getState().setOpen(true);
        usePendingSkillRunStore.getState().setPendingSkillRun({ ...skillFlow, canvasId });
      }
    })();
  }, [
    boardId,
    commitDraftCanvas,
    currentWorkspace?.id,
    generativeCanvasEnabled,
    openDraftCanvas,
    router.isReady,
    router.query,
  ]);
};
